import React, { useEffect } from 'react';
import c from 'classnames';
import _debounce from 'lodash/debounce';
import { baseKeymap } from 'prosemirror-commands';
import { dropCursor } from 'prosemirror-dropcursor';
import { buildKeymap } from 'prosemirror-example-setup';
import { gapCursor } from 'prosemirror-gapcursor';
import { history, redo, undo } from 'prosemirror-history';
import { inputRules as _inputRules } from 'prosemirror-inputrules';
import { EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { redo as yRedo, undo as yUndo, ySyncPlugin, yUndoPlugin } from 'y-prosemirror';

import { WithTranslation, withTranslation } from '@lyearn/i18n';

import { NodeStatesContext, useNodeStates } from '../EditorNodeStates';
import keymap from '../ExtensionManager/keymap';
import { EditorOptions } from '../ExtensionManager/types';
import { FileBuffer, FileBufferContext } from '../FileBuffer';
import { NodePerformanceContext } from '../Performance';
import { getRequiredPlugin } from '../Plugin/consts';
import { editorStatsPlugin, getBlockWords } from '../Plugin/editorStatsPlugin';
import { nodePerformanceMapPlugin } from '../Plugin/nodePerformanceMapPlugin';
import { portalKeyPlugin } from '../Plugin/portalKeyPlugin';
import yCursorPlugin from '../Plugin/yCursorPlugin';
import { getCreateReactNodeView } from '../ReactNodeView';
import ReactNodeViewPortalsProvider, {
  IReactNodeViewPortalsContext,
  ReactNodeViewPortalsContext,
} from '../ReactNodeViewPortals';
import { RequestContext } from '../RequestProvider';
import { Storage, StorageContext } from '../Storage';
import { replaceEditorPlaceholderWithDefaultVideo, seedYDocWithDefaultVideo } from '../utils/index';
import { editableEditorStyles, readOnlyEditorStyles } from './styles';
import { EditorProps } from './types';

const fileBuffer = new FileBuffer();

const storage = new Storage();

class Editor extends React.Component<EditorProps & WithTranslation, any> {
  private readonly editorViewRef: React.RefObject<HTMLDivElement> = React.createRef();
  private view?: EditorView;
  static contextType = ReactNodeViewPortalsContext;
  static defaultProps = { editable: true, loader: null };

  loadEditor = _debounce(() => {
    const portalApi = this.context as IReactNodeViewPortalsContext;
    const { createPortal, destroyPortal } = portalApi;
    if (this.view) {
      return null;
    }

    const createReactNodeView = getCreateReactNodeView(createPortal, destroyPortal);

    const editorViewDOM = this.editorViewRef.current;
    const {
      schema,
      onChange,
      onWordsStatsChange,
      onCreateEditorView,
      doc,
      nodeViews,
      plugins = [],
      yProvider,
      nodePerformanceMap,
      t,
      inputRules,
      isMicroLearning = false,
      isByteContent,
      isLocalizedContent = false,
      isNewlyCreatedMicroLearning,
      ...rest
    } = this.props;
    const editorOptions: EditorOptions = {
      schema,
      createReactNodeView,
      portalApi,
      t,
      isLocalizedContent,
      isMicroLearning,
      isByteContent,
    };

    const editableFn = typeof rest.editable === 'function' ? rest.editable : () => !!rest.editable;
    const type = yProvider?.doc.getXmlFragment('prosemirror');
    // Provider is only exposed after Yjs sync, so seeding here is post-sync.
    // Still replace empty-paragraph placeholders that sync often leaves behind.
    if (isMicroLearning && isNewlyCreatedMicroLearning) {
      seedYDocWithDefaultVideo(schema, yProvider);
    }
    let state = EditorState.create({
      schema,
      doc,
      plugins: [
        ...(yProvider
          ? [
              ySyncPlugin(type!),
              yCursorPlugin(yProvider.awareness),
              yUndoPlugin(),
              keymap({ 'Mod-z': yUndo, 'Mod-y': yRedo, 'Mod-Shift-z': yRedo }),
            ]
          : [history(), keymap({ 'Mod-z': undo, 'Mod-y': redo, 'Mod-Shift-z': redo })]),
        ...plugins?.flatMap((plugin) =>
          typeof plugin === 'function' ? plugin?.(editorOptions) : plugin,
        ),
        keymap(buildKeymap(schema)),
        keymap(baseKeymap),
        dropCursor(),
        gapCursor(),
        _inputRules({
          rules: inputRules?.length ? inputRules?.flatMap((rule) => rule(editorOptions)) : [],
        }),
        nodePerformanceMapPlugin(nodePerformanceMap),
        portalKeyPlugin(portalApi),
        editorStatsPlugin(),
      ],
    });

    const isEditable = editableFn(state);

    if (!isEditable || isLocalizedContent) {
      // keep only nodeView plugin active in readonly mode
      const viewerAllowedPluginKeys = getRequiredPlugin(isLocalizedContent);
      state = state.reconfigure({
        plugins: [
          ...state.plugins.filter(
            (plugin) =>
              plugin.props.nodeViews ||
              (plugin.spec.key &&
                (viewerAllowedPluginKeys.includes(plugin.spec.key) ||
                  this.props.enabledPluginsInReadOnly?.includes(plugin.spec.key))),
          ),
          ...(isLocalizedContent
            ? [keymap({ 'Mod-z': yUndo, 'Mod-y': yRedo, 'Mod-Shift-z': yRedo })]
            : []),
        ],
      });
    }

    this.view = new EditorView(editorViewDOM!, {
      state,
      nodeViews,
      editable: editableFn,
      // no view plugins are used
      plugins: [],
      dispatchTransaction: function dispatchTransaction(transaction) {
        const newState = this.state.apply(transaction);

        // @ts-ignore
        if (newState && this.docView) {
          // @ts-ignore
          this.updateState(newState);
          onChange?.(newState);
          if (transaction.docChanged) {
            onWordsStatsChange?.(getBlockWords(newState.doc));
          }
        }
      },
    });

    // dispatch tr on creation for added id to all blocks in all cases
    this.view.dispatch(this.view.state.tr);

    // y-prosemirror can re-materialize an empty paragraph when the view binds; replace
    // that placeholder with the default empty video for newly created microlearning.
    if (isMicroLearning && isNewlyCreatedMicroLearning) {
      replaceEditorPlaceholderWithDefaultVideo(this.view, schema);
    }

    onCreateEditorView?.(this.view);
  });

  componentDidMount() {
    this.loadEditor();
  }

  componentWillUnmount() {
    this.view?.destroy();
    this.view = undefined;
  }

  render() {
    return (
      <div
        ref={this.editorViewRef}
        className={c(
          this.view?.editable ? 'Prosemirror-Editable' : 'Prosemirror-Readonly',
          this.view?.editable
            ? editableEditorStyles(this.props.t('Lygo:Menu.math_display.WriteYourEquationInKaTex'))
            : readOnlyEditorStyles(this.props.t('Lygo:Menu.math_display.WriteYourEquationInKaTex')),
          'lygo-editor',
        )}
      />
    );
  }
}

// @ts-ignore
const EditorWithI18n = withTranslation('Lygo')(Editor);

const ProseMirrorWrapper: React.FC<EditorProps> = (props) => {
  const {
    setNodePerformance,
    getRequest,
    getNodePerformance,
    useConvertFile,
    postRequest,
    enabledConvertibleImport,
  } = props;
  const { setNodeState, getNodeState } = useNodeStates();

  useEffect(() => {
    return () => {
      fileBuffer.clear();
    };
  }, []);

  return (
    <RequestContext.Provider
      value={{
        getRequest,
        postRequest,
        useConvertFile,
        enabledConvertibleImport,
      }}>
      <StorageContext.Provider value={{ storage }}>
        <FileBufferContext.Provider value={{ fileBuffer }}>
          <NodeStatesContext.Provider value={{ setNodeState, getNodeState }}>
            <NodePerformanceContext.Provider value={{ setNodePerformance, getNodePerformance }}>
              <ReactNodeViewPortalsProvider>
                <EditorWithI18n {...props} />
              </ReactNodeViewPortalsProvider>
            </NodePerformanceContext.Provider>
          </NodeStatesContext.Provider>
        </FileBufferContext.Provider>
      </StorageContext.Provider>
    </RequestContext.Provider>
  );
};

export default ProseMirrorWrapper;
